import React, { useState, useEffect } from 'react';
import { AggregatedRating } from '../types';
import { fetchAggregatedRatings } from '../services/ratingsService';
import { XIcon, StarIcon, MapPinIcon } from 'shared-icons';

interface RatingsPageProps {
  isOpen: boolean;
  onClose: () => void;
}

const StarRatingDisplay: React.FC<{ rating: number }> = ({ rating }) => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
    return (
      <div className="flex items-center text-yellow-400">
        {[...Array(fullStars)].map((_,i)=><StarIcon key={`f-${i}`} filled width={20} height={20}/>)}
        {halfStar && <div className="relative"><StarIcon key="h" width={20} height={20}/><div className="absolute top-0 left-0 overflow-hidden w-1/2"><StarIcon key="hf" filled width={20} height={20}/></div></div>}
        {[...Array(emptyStars)].map((_,i)=><StarIcon key={`e-${i}`} width={20} height={20} className="text-gray-300 dark:text-slate-600"/>)}
      </div>
    );
};

const RatingsPage: React.FC<RatingsPageProps> = ({ isOpen, onClose }) => {
  const [ratings, setRatings] = useState<AggregatedRating[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const loadRatings = async () => {
        try { setIsLoading(true); setError(null); setRatings(await fetchAggregatedRatings()); }
        catch (e) { setError('Failed to load ratings data. Please try again later.'); }
        finally { setIsLoading(false); }
      };
      loadRatings();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white dark:bg-slate-900 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="ratings-title" style={{ animation: 'modal-fade-in 0.3s' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <header className="flex justify-between items-center mb-8">
          <div><h1 id="ratings-title" className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">ISP Performance Ratings</h1><p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Based on anonymous user feedback from this tool.</p></div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700/50 transition-colors" aria-label="Close ratings page"><XIcon className="w-8 h-8 text-slate-600 dark:text-slate-300" /></button>
        </header>
        <main>
          {isLoading && <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div><p className="mt-4 text-lg text-slate-600 dark:text-slate-400">Loading Ratings...</p></div>}
          {error && <div className="text-center py-12 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-4 rounded-lg"><p className="font-bold">An Error Occurred</p><p>{error}</p></div>}
          {!isLoading && !error && (
            <div className="space-y-4">
              {ratings.map(rating => (
                <div key={rating.id} className="bg-gray-50 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700 rounded-lg p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center">
                    <div className="mb-4 sm:mb-0"><h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">{rating.isp}</h2><div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400 mt-1"><MapPinIcon /><span>{rating.location}</span>{rating.as && <span className="font-mono text-xs">({rating.as})</span>}</div></div>
                    <div className="flex items-center gap-4">
                      <div className="text-right"><StarRatingDisplay rating={rating.averageRating} /><p className="text-sm font-bold text-slate-700 dark:text-slate-200 mt-1">{rating.averageRating.toFixed(2)} <span className="font-normal text-xs text-gray-500 dark:text-slate-400">avg</span></p></div>
                      <div className="w-px h-10 bg-gray-200 dark:bg-slate-700"></div>
                      <div className="text-center"><p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{rating.ratingCount}</p><p className="text-xs text-gray-500 dark:text-slate-400 -mt-1">ratings</p></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default RatingsPage;